import { useState } from "react";
import { ShieldCheck, Heart, ArrowUpRight } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { DONATE_URL } from "../lib/config";

const PRESETS = ["25", "50", "100"];

export default function Donate() {
  const [amount, setAmount] = useState("50");
  const [custom, setCustom] = useState("");
  const [isOther, setIsOther] = useState(false);

  const selectPreset = (v) => {
    setAmount(v);
    setIsOther(false);
    setCustom("");
  };

  const selectOther = () => {
    setIsOther(true);
    setAmount("");
  };

  const finalAmount = isOther ? custom : amount;
  const linkConfigured = Boolean(DONATE_URL);

  const donateHref = linkConfigured
    ? `${DONATE_URL}${DONATE_URL.includes("?") ? "&" : "?"}amount=${encodeURIComponent(finalAmount || "")}`
    : undefined;

  return (
    <div data-testid="donate-page">
      <PageHero
        eyebrow="Support the Campaign"
        title="Support the Campaign"
        intro="Your support helps keep experienced, accountable leadership in the Treasurer's Office."
        testid="donate-hero"
      />

      <section className="py-20 md:py-28 bg-background">
        <div className="max-w-6xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12">
          {/* Form */}
          <div className="lg:col-span-7">
            <Reveal>
              <div className="bg-white border border-slate-200 rounded-sm p-8 md:p-10">
                <p className="text-sm uppercase tracking-overline font-bold text-red-700">Choose an amount</p>
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {PRESETS.map((v) => (
                    <button
                      key={v}
                      onClick={() => selectPreset(v)}
                      data-testid={`donate-amount-${v}`}
                      className={`focus-ring rounded-sm border py-5 font-display text-2xl font-bold transition-all ${
                        !isOther && amount === v
                          ? "bg-[hsl(var(--navy))] text-white border-[hsl(var(--navy))] -translate-y-0.5 shadow-md"
                          : "bg-white text-[hsl(var(--navy))] border-slate-300 hover:border-[hsl(var(--navy))]"
                      }`}
                    >
                      ${v}
                    </button>
                  ))}
                  <button
                    onClick={selectOther}
                    data-testid="donate-amount-other"
                    className={`focus-ring rounded-sm border py-5 font-display text-2xl font-bold transition-all ${
                      isOther
                        ? "bg-[hsl(var(--navy))] text-white border-[hsl(var(--navy))] -translate-y-0.5 shadow-md"
                        : "bg-white text-[hsl(var(--navy))] border-slate-300 hover:border-[hsl(var(--navy))]"
                    }`}
                  >
                    Other
                  </button>
                </div>

                {isOther && (
                  <div className="mt-5">
                    <label className="text-sm font-medium text-slate-600" htmlFor="custom-amount">
                      Enter an amount
                    </label>
                    <div className="mt-2 flex items-center border border-slate-300 rounded-sm focus-within:ring-2 focus-within:ring-red-700 focus-within:ring-offset-2 overflow-hidden">
                      <span className="px-4 text-lg font-semibold text-slate-500 bg-slate-50 self-stretch flex items-center">$</span>
                      <input
                        id="custom-amount"
                        type="number"
                        min="1"
                        inputMode="numeric"
                        value={custom}
                        onChange={(e) => setCustom(e.target.value)}
                        placeholder="Amount"
                        data-testid="donate-custom-input"
                        className="flex-1 px-4 py-3.5 text-lg outline-none"
                      />
                    </div>
                  </div>
                )}

                {linkConfigured ? (
                  <a
                    href={donateHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="donate-now-btn"
                    className="focus-ring group mt-8 w-full inline-flex items-center justify-center gap-2 bg-red-700 text-white font-semibold px-8 py-4 rounded-sm hover:bg-red-800 transition-colors"
                  >
                    <Heart size={18} />
                    Donate {finalAmount ? `$${finalAmount}` : "Now"}
                    <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </a>
                ) : (
                  <div className="mt-8">
                    <button
                      disabled
                      data-testid="donate-now-btn"
                      className="w-full inline-flex items-center justify-center gap-2 bg-slate-300 text-white font-semibold px-8 py-4 rounded-sm cursor-not-allowed"
                    >
                      <Heart size={18} />
                      Donate {finalAmount ? `$${finalAmount}` : "Now"}
                    </button>
                    <p className="mt-3 text-sm text-slate-500 text-center" data-testid="donate-link-note">
                      Online donations open soon — the secure payment link will be added here.
                    </p>
                  </div>
                )}

                <p className="mt-6 flex items-center gap-2 text-xs text-slate-400">
                  <ShieldCheck size={16} className="text-slate-400" />
                  Donations are processed on an external secure payment page.
                </p>
              </div>
            </Reveal>
          </div>

          {/* Cause panel */}
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="bg-[hsl(var(--navy))] text-white rounded-sm p-8 md:p-10 h-full paper-grain">
                <p className="text-sm uppercase tracking-overline font-bold text-red-400">Why it matters</p>
                <h2 className="mt-5 font-display text-3xl md:text-4xl font-bold tracking-tight text-balance">
                  Every contribution keeps the office accountable.
                </h2>
                <p className="mt-5 text-slate-300 leading-relaxed">
                  Ontario County deserves a Treasurer's Office run with fairness, consistency, and
                  transparency. Your support powers the reelection campaign and the continued modernization
                  of the department.
                </p>
                <ul className="mt-8 space-y-4">
                  {[
                    "Experienced leadership taxpayers can trust",
                    "Proven results across four-plus years",
                    "Accountability at every level of the office",
                  ].map((point) => (
                    <li key={point} className="flex items-start gap-3">
                      <span className="mt-1 text-red-400">&#9733;</span>
                      <span className="text-slate-200">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
